import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Edit, AlertCircle, X, Eye, Copy, Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { SessionType } from '@shared';
import { useJsonErrorHandler } from '@/hooks/useJsonErrorHandler';
import { useFormActions } from '@/hooks/useFormActions';
import { useVisualEditor } from '@/hooks/useVisualEditor';
import { VisualEditor } from './VisualEditor';
import { FormDiffViewer } from './FormDiffViewer';
import { useFormDifferenceView } from '@client/hooks/useFormDifferenceView';

interface FormPreviewModeProps {
  currentFormDefinition: PolicyFormDefinition;
  onFormDefinitionChange: (newDefinition: PolicyFormDefinition | undefined) => void;
  hasOverride: boolean;
  originalFormDefinition: PolicyFormDefinition;
  onBannerVisibilityChange?: (visible: boolean) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function FormPreviewMode({
  currentFormDefinition,
  onFormDefinitionChange,
  hasOverride,
  originalFormDefinition,
  onBannerVisibilityChange,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
}: FormPreviewModeProps) {
  const session = useSelector(selectSession);
  const isWizard = session.sessionType === SessionType.wizard;

  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use external control if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalOnOpenChange || setInternalIsOpen;
  const [sectionsJsonText, setSectionsJsonText] = useState('');
  const [questionsJsonText, setQuestionsJsonText] = useState('');
  const [activeTab, setActiveTab] = useState('visual');

  const { error, errorContext, setError, clearError, handleJsonError } = useJsonErrorHandler();

  const { copied, handleApplyChanges, handleCopyFormDefinition, handleClearOverride } =
    useFormActions({
      sectionsJsonText,
      questionsJsonText,
      currentFormDefinition,
      hasOverride,
      onFormDefinitionChange,
      setError,
      clearError,
      handleJsonError,
      setIsOpen,
      setSectionsJsonText,
      setQuestionsJsonText,
      setActiveTab,
    });

  const {
    handleSectionChange,
    handleSubsectionChange,
    handleQuestionGroupChange,
    handleQuestionChange,
    handleAddQuestionToGroup,
    handleAddGroup,
    handleAddSubsection,
    handleAddSection,
    handleRemoveQuestion,
    handleRemoveGroup,
    handleRemoveSubsection,
    handleRemoveSection,
    confirmDelete,
    setConfirmDelete,
  } = useVisualEditor({
    sections: currentFormDefinition.sections,
    questions: currentFormDefinition.questions,
    onFormDefinitionChange,
    currentFormDefinition,
  });

  const {
    differences,
    hierarchicalDifference,
    copied: formDiffCopy,
    handleCopyFormDifference,
  } = useFormDifferenceView({
    currentFormDefinition,
    originalFormDefinition,
  });

  const isBannerVisible = differences?.length > 0;

  useEffect(() => {
    onBannerVisibilityChange?.(isBannerVisible);
  }, [isBannerVisible, onBannerVisibilityChange]);

  useEffect(() => {
    if (isOpen) {
      setSectionsJsonText(JSON.stringify(currentFormDefinition.sections, null, 2));
      setQuestionsJsonText(JSON.stringify(currentFormDefinition.questions, null, 2));
    }
  }, [isOpen, currentFormDefinition]);

  // Only show for wizard session type
  if (!isWizard) {
    return null;
  }

  return (
    <>
      {/* Preview Mode Banner */}
      {differences?.length > 0 && (
        <div className="bg-accent fixed left-0 right-0 top-0 z-50 border-b">
          <div className="flex items-center justify-center px-4 py-2">
            <Eye className="text-primary-dark mr-2 h-4 w-4" />
            <span className="text-primary-dark text-sm font-medium">
              Form Preview Mode Active - This form is using a custom JSON definition
            </span>
          </div>
        </div>
      )}

      {/* Edit Button */}
      <Dialog
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Form Preview Mode</DialogTitle>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto">
            {differences?.length > 0 && (
              <Alert className="border-blue-200 bg-blue-50">
                <AlertDescription className="text-sm">
                  Preview mode is active. The form is using your custom JSON definition. Clear
                  override to return to the original form.
                </AlertDescription>
              </Alert>
            )}

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="visual">Visual Editor</TabsTrigger>
                <TabsTrigger value="differenceTree">Difference Tree</TabsTrigger>
                {/* <TabsTrigger value="sections">Sections JSON</TabsTrigger>
                <TabsTrigger value="questions">Questions JSON</TabsTrigger> */}
              </TabsList>

              <TabsContent value="visual">
                <VisualEditor
                  sections={currentFormDefinition.sections}
                  questions={currentFormDefinition.questions}
                  onSectionChange={handleSectionChange}
                  onSubsectionChange={handleSubsectionChange}
                  onQuestionGroupChange={handleQuestionGroupChange}
                  onQuestionChange={handleQuestionChange}
                  onAddQuestionToGroup={handleAddQuestionToGroup}
                  onAddGroup={handleAddGroup}
                  onAddSubsection={handleAddSubsection}
                  onAddSection={handleAddSection}
                  onRemoveQuestion={handleRemoveQuestion}
                  onRemoveGroup={handleRemoveGroup}
                  onRemoveSubsection={handleRemoveSubsection}
                  onRemoveSection={handleRemoveSection}
                  confirmDelete={confirmDelete}
                  setConfirmDelete={setConfirmDelete}
                />
              </TabsContent>

              <TabsContent value="differenceTree">
                <FormDiffViewer
                  changes={differences}
                  hierarchicalDiffs={hierarchicalDifference}
                />
              </TabsContent>

              {/* <TabsContent value="sections">
                <JsonEditor
                  id="sections-editor"
                  label="Sections JSON"
                  value={sectionsJsonText}
                  placeholder="Edit sections array here..."
                  onChange={setSectionsJsonText}
                />
              </TabsContent>

              <TabsContent value="questions">
                <JsonEditor
                  id="questions-editor"
                  label="Questions JSON"
                  value={questionsJsonText}
                  placeholder="Edit questions object here..."
                  onChange={setQuestionsJsonText}
                />
              </TabsContent> */}
            </Tabs>
          </div>

          {/* Sticky Error Alert */}
          {error && (
            <div className="border-t bg-white p-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <div className="font-medium">{error}</div>
                  {errorContext && (
                    <pre className="overflow-x-auto whitespace-pre-wrap rounded border bg-gray-100 p-2 text-xs">
                      {errorContext}
                    </pre>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Sticky Footer */}
          <div className="flex justify-between gap-2 border-t bg-white p-4">
            <div className="flex gap-2">
              {/* <Button
                onClick={handleApplyChanges}
                disabled={!sectionsJsonText.trim() || !questionsJsonText.trim()}
              >
                Apply Changes
              </Button> */}
              <Button
                variant="outline"
                onClick={handleCopyFormDefinition}
              >
                {copied ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Form Definition
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleCopyFormDifference}
              >
                {formDiffCopy ? (
                  <>
                    <Check className="mr-2 h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="mr-2 h-4 w-4" />
                    Copy Form Difference
                  </>
                )}
              </Button>
              {differences?.length > 0 && (
                <Button
                  variant="destructive"
                  onClick={handleClearOverride}
                >
                  <X className="mr-2 h-4 w-4" />
                  Reset to Original
                </Button>
              )}
            </div>
            {/* <Button
              variant="ghost"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button> */}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
